import { t } from "@lingui/macro";
import { useMemo } from "react";

import {
  InfoIcon,
  PlaceIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";
import { dateUtils, restrictionUtils } from "@whoosh/utilities";

import { CustomColor } from "../../../../../Events/components/TimeListEventsCard/__generated__/TimeListEventsCard_EventSession.graphql";

export type RestrictionInfoBarProps = {
  id: string;
  name: string;
  displayName: string | null;
  startAt: string | null;
  endAt: string | null;
  allDay: boolean;
  visibilityColor?: CustomColor | null;
  courseMessage?: string | null;
  memberMessage?: string | null;
};

export const RestrictionInfoBar = ({
  name,
  startAt,
  endAt,
  allDay,
  courseMessage,
  visibilityColor,
  displayName,
  memberMessage,
}: RestrictionInfoBarProps) => {
  const { startAt: restrictionStartAt, endAt: restrictionEndAt } =
    restrictionUtils.getTimeOperatorAndStartEndAt(startAt, endAt);

  const title = displayName || name;

  const color: WhooshThemeColors = `surface-user-selectable-color${
    visibilityColor ?? "A"
  }-subtlest`;

  const formatStartTime = restrictionStartAt
    ? dateUtils.formatHHMM(restrictionStartAt)
    : "";
  const formatEndTime = restrictionEndAt
    ? dateUtils.formatHHMM(restrictionEndAt)
    : "";

  const timeValue = useMemo(() => {
    if (allDay) return t`All Day`;
    if (formatStartTime && formatEndTime)
      return `${formatStartTime} - ${formatEndTime}`;
    if (formatStartTime) return `${t`Starts at`} ${formatStartTime}`;
    if (formatEndTime) return `${t`Ends at`} ${formatEndTime}`;
    return "";
  }, [allDay, formatStartTime, formatEndTime]);

  return (
    <Box
      backgroundColor="surface-primary"
      marginTop={2}
      marginBottom={4}
      px={1}
      py={1}
      style={{ borderRadius: 12 }}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack alignItems="center" gap={4}>
          <Box
            width={40}
            height={40}
            backgroundColor={color}
            borderRadius={"2"}
            justifyContent="center"
            alignItems="center"
          >
            <InfoIcon color="black" />
          </Box>
          <VStack>
            {title ? <Text variant="mainSmallSemiBold">{title}</Text> : null}
            {timeValue ? (
              <Text variant="mainSmallSemiBold" color="text-secondary">
                {timeValue}
              </Text>
            ) : null}
          </VStack>
        </HStack>
        <HStack alignItems="center" gap={1} mr={3}>
          <Text variant="mainTiny">{courseMessage}</Text>
          <PlaceIcon color="black" />
        </HStack>
      </HStack>
      {memberMessage ? (
        <Text variant="mainBaseMedium" mt={2}>
          {memberMessage}
        </Text>
      ) : null}
    </Box>
  );
};
